import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Download,
  Eye,
  Mail,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  RefreshCw,
  Edit,
  X,
  FileText,
  Trash2,
  Square,
  CheckSquare,
  UserPlus,
  Ticket,
  MessageCircle,
  Plus,
  Trash,
  Send,
  Settings,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
};

const SOURCE_LABELS = {
  stripe: "Stripe",
  manual: "Manual",
  interac_e_transfer: "Interac",
  cash: "Cash",
};

const AdminMarketCampRegistrations = () => {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [editingReg, setEditingReg] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  const loadAll = async () => {
    try {
      const [r, w] = await Promise.all([
        axios.get(`${API}/admin/market-camp-registrations`),
        axios.get(`${API}/admin/whatsapp-config-status`).catch(() => ({ data: { twilio_configured: false } })),
      ]);
      return { regs: r.data || [], waOn: !!w.data?.twilio_configured };
    } catch {
      toast.error("Failed to load registrations");
      return null;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const d = await loadAll();
    if (d) { setRegs(d.regs); setWhatsappConfigured(d.waOn); }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const d = await loadAll();
      if (active && d) { setRegs(d.regs); setWhatsappConfigured(d.waOn); setLoading(false); }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtering
  const filtered = regs.filter(r => {
    if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
    if (sourceFilter !== "all") {
      const src = r.source || (r.checkout_session_id ? "stripe" : "manual");
      if (src !== sourceFilter) return false;
    }
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.registration_code?.toLowerCase().includes(q) ||
      r.parent_full_name?.toLowerCase().includes(q) ||
      r.parent_email?.toLowerCase().includes(q) ||
      r.parent_phone?.includes(q) ||
      (r.children || []).some(c =>
        c.full_name?.toLowerCase().includes(q) ||
        c.ticket_code?.toLowerCase().includes(q)
      )
    );
  });

  // Stats (matching Suds layout: Total Revenue / Total Guests / Total Bookings)
  const paid = filtered.filter(r => r.payment_status === "paid");
  const totalRevenue = paid.reduce((s, r) => s + (r.total || 0), 0);
  const totalGuests = paid.reduce((s, r) => s + (r.num_children || 0), 0);
  const totalBookings = filtered.length;

  // Source-based tabs (mirroring "Landing Page / Event / Experience" tabs)
  const stripeRegs = regs.filter(r => (r.source || (r.checkout_session_id ? "stripe" : "manual")) === "stripe");
  const manualRegs = regs.filter(r => ["manual", "cash", "interac_e_transfer"].includes(r.source || ""));

  // 📊 MARKET INTELLIGENCE — age-group booking distribution (paid + manual only)
  const ageGroupStats = (() => {
    const counts = { "4-7": 0, "8-11": 0, "12-15": 0, "Other": 0 };
    const revenueByGroup = { "4-7": 0, "8-11": 0, "12-15": 0, "Other": 0 };
    let totalKids = 0;
    paid.forEach((r) => {
      const childCount = (r.children || []).length || 0;
      const perKidShare = childCount > 0 ? (r.total || 0) / childCount : 0;
      (r.children || []).forEach((c) => {
        const g = ["4-7", "8-11", "12-15"].includes(c.age_group) ? c.age_group : "Other";
        counts[g] += 1;
        revenueByGroup[g] += perKidShare;
        totalKids += 1;
      });
    });
    return { counts, revenueByGroup, totalKids };
  })();

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    selectedIds.length === filtered.length
      ? setSelectedIds([])
      : setSelectedIds(filtered.map(r => r.id));

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/sync-stripe`);
      if (res.data.updated > 0) toast.success(`Synced ${res.data.updated} payments from Stripe!`);
      else if (res.data.checked > 0) toast.info(`Checked ${res.data.checked} bookings — no new payments`);
      else toast.info("No pending bookings to check");
      fetchData();
    } catch {
      toast.error("Failed to sync with Stripe");
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = () => {
    window.open(`${API}/admin/market-camp-registrations/export`, "_blank");
  };

  const resendEmail = async (id) => {
    try {
      await axios.post(`${API}/admin/market-camp-registrations/${id}/resend-email`);
      toast.success("Confirmation email re-sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend email");
    }
  };

  const resendWhatsApp = async (id) => {
    if (whatsappConfigured) {
      try {
        await axios.post(`${API}/admin/market-camp-registrations/${id}/resend-whatsapp`);
        toast.success("WhatsApp sent to parent");
        fetchData();
        return;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "WhatsApp failed — opening manual chat");
      }
    }
    try {
      const r = await axios.get(`${API}/admin/market-camp-registrations/${id}/whatsapp-link`);
      if (!r.data.parent_whatsapp_url) { toast.error("No parent phone on file."); return; }
      window.open(r.data.parent_whatsapp_url, "_blank");
    } catch { toast.error("Failed to open WhatsApp"); }
  };

  const confirmBooking = async (id) => {
    try {
      await axios.patch(`${API}/admin/market-camp-registrations/${id}`, { payment_status: "paid" });
      toast.success("Marked as paid · sending confirmation");
      // Trigger email + whatsapp
      await Promise.all([
        resendEmail(id).catch(() => null),
        whatsappConfigured ? resendWhatsApp(id).catch(() => null) : null,
      ]);
      fetchData();
    } catch {
      toast.error("Failed to confirm booking");
    }
  };

  const deleteReg = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/market-camp-registrations/${deleteId}`);
      toast.success("Registration deleted");
      setDeleteId(null);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const bulkDelete = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API}/admin/market-camp-registrations/${id}`)));
      toast.success(`Deleted ${selectedIds.length} registrations`);
      setSelectedIds([]); setShowBulkDelete(false); fetchData();
    } catch { toast.error("Bulk delete failed"); }
  };

  const bulkEmail = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/bulk-resend-email`, { registration_ids: selectedIds });
      toast.success(`Email queued for ${res.data.queued} parent(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk email failed"); }
  };

  const bulkWhatsApp = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/bulk-resend-whatsapp`, { registration_ids: selectedIds });
      toast.success(`WhatsApp queued for ${res.data.queued} parent(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk WhatsApp failed"); }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-market-camp-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Market Camp Bookings
            </h1>
            <p className="text-[#A1A1AA]">Manage all confirmed bookings (Stripe-confirmed + manual entries)</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {selectedIds.length > 0 && (
              <>
                <button onClick={bulkEmail} className="px-4 py-2 bg-blue-900/30 text-blue-400 border border-blue-500/40 font-medium flex items-center gap-2" data-testid="bulk-email-btn">
                  <Mail className="w-5 h-5" /> Email ({selectedIds.length})
                </button>
                <button onClick={bulkWhatsApp} className="px-4 py-2 bg-green-900/30 text-green-400 border border-green-500/40 font-medium flex items-center gap-2" data-testid="bulk-whatsapp-btn">
                  <MessageCircle className="w-5 h-5" /> WhatsApp ({selectedIds.length})
                </button>
                <button onClick={() => setShowBulkDelete(true)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium flex items-center gap-2" data-testid="bulk-delete-btn">
                  <Trash2 className="w-5 h-5" /> Delete ({selectedIds.length})
                </button>
              </>
            )}
            <Link to="/admin/market-camp-tickets" className="btn-secondary flex items-center gap-2" data-testid="link-camp-tickets">
              <Ticket className="w-5 h-5" /> Tickets
            </Link>
            <Link to="/admin/market-camp-groups" className="btn-secondary flex items-center gap-2" data-testid="link-camp-groups">
              <Users className="w-5 h-5" /> Groups
            </Link>
            <Link to="/admin/market-camp-settings" className="btn-secondary flex items-center gap-2" data-testid="link-camp-settings">
              <Settings className="w-5 h-5" /> Camp Settings
            </Link>
            <button onClick={() => setShowAddManual(true)} className="btn-secondary flex items-center gap-2" data-testid="add-manual-booking-btn">
              <UserPlus className="w-5 h-5" /> Add Guest Manually
            </button>
            <button onClick={syncStripe} disabled={syncing} className="btn-primary flex items-center gap-2" data-testid="sync-stripe-btn">
              <RefreshCw className={`w-5 h-5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing..." : "Sync Stripe"}
            </button>
            <button onClick={handleExport} className="btn-secondary flex items-center gap-2" data-testid="export-bookings-btn">
              <Download className="w-5 h-5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Tabs — Source-based count tabs (mirrors Suds tab pattern) */}
        <div className="flex gap-4 border-b border-[rgba(212,175,55,0.15)] overflow-x-auto">
          <button onClick={() => setSourceFilter("all")} className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${sourceFilter === "all" ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"}`} data-testid="tab-all">
            All Bookings
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">{regs.length}</span>
            {sourceFilter === "all" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />}
          </button>
          <button onClick={() => setSourceFilter("stripe")} className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${sourceFilter === "stripe" ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"}`} data-testid="tab-stripe">
            Stripe Bookings
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">{stripeRegs.length}</span>
            {sourceFilter === "stripe" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />}
          </button>
          <button onClick={() => setSourceFilter("manual")} className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${["manual", "interac_e_transfer", "cash"].includes(sourceFilter) ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"}`} data-testid="tab-manual">
            Manual Entries
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">{manualRegs.length}</span>
            {["manual", "interac_e_transfer", "cash"].includes(sourceFilter) && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />}
          </button>
        </div>

        {!whatsappConfigured && (
          <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-xs text-yellow-300" data-testid="whatsapp-warning">
            ⚠️ <strong>Automated WhatsApp not configured.</strong> Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and <code>TWILIO_WHATSAPP_FROM</code> in backend/.env to enable server-side sending. Until then, WhatsApp buttons open a pre-filled chat for manual sending.
          </div>
        )}

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="stat-revenue">C${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Guests</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="stat-guests">{totalGuests}</p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="stat-bookings">{totalBookings}</p>
          </div>
        </div>

        {/* 📊 MARKET INTELLIGENCE — Age Group Distribution */}
        <MarketIntelligenceChart stats={ageGroupStats} />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search by name, email, code, or ticket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 focus:border-[#D4AF37] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
            data-testid="status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
            <Calendar className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Bookings Found</h3>
            <p className="text-[#A1A1AA]">No bookings match your search criteria</p>
          </div>
        ) : (
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="registrations-table">
                <thead>
                  <tr className="border-b border-[rgba(212,175,55,0.15)] bg-[#0F0F0F]">
                    <th className="py-4 px-4 w-12">
                      <button onClick={toggleSelectAll} className="p-1 text-[#A1A1AA] hover:text-[#D4AF37]" data-testid="select-all-bookings">
                        {selectedIds.length === filtered.length && filtered.length > 0 ? <CheckSquare className="w-5 h-5 text-[#D4AF37]" /> : <Square className="w-5 h-5" />}
                      </button>
                    </th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Customer</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Children / Tickets</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Qty</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Amount</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Source</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Status</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Date</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((reg) => {
                    const src = reg.source || (reg.checkout_session_id ? "stripe" : "manual");
                    const hasAllergies = (reg.children || []).some(c => c.allergies && c.allergies.trim().toLowerCase() !== "none");
                    return (
                      <motion.tr
                        key={reg.id}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className={`border-b border-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.02)] ${selectedIds.includes(reg.id) ? "bg-[rgba(212,175,55,0.05)]" : ""}`}
                        data-testid={`reg-row-${reg.id}`}
                      >
                        <td className="py-4 px-4">
                          <button onClick={() => toggleSelect(reg.id)} className="p-1 text-[#A1A1AA] hover:text-[#D4AF37]" data-testid={`select-reg-${reg.id}`}>
                            {selectedIds.includes(reg.id) ? <CheckSquare className="w-5 h-5 text-[#D4AF37]" /> : <Square className="w-5 h-5" />}
                          </button>
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-[#F5F5F0] font-medium">{reg.parent_full_name}</p>
                          <p className="text-[#a1a1aa] text-sm">{reg.parent_email}</p>
                          <p className="text-[#a1a1aa] text-sm">{reg.parent_phone}</p>
                          <p className="text-[#D4AF37] text-xs font-mono mt-1">{reg.registration_code}</p>
                          {hasAllergies && (
                            <p className="text-red-400 text-xs mt-0.5 flex items-center gap-1" title="Has allergies"><AlertTriangle className="w-3 h-3" /> ALLERGY</p>
                          )}
                          {(reg.pickup_id_storage_path && reg.pickup_selfie_storage_path) ? (
                            <p className="text-green-400 text-[10px] mt-0.5 flex items-center gap-1 font-bold uppercase tracking-wider" title="Pickup ID + selfie on file" data-testid={`pickup-verified-${reg.id}`}>
                              <CheckCircle className="w-3 h-3" /> Pickup ID verified
                            </p>
                          ) : (
                            <p className="text-yellow-400 text-[10px] mt-0.5 flex items-center gap-1 font-bold uppercase tracking-wider" title="No pickup photos on file" data-testid={`pickup-missing-${reg.id}`}>
                              <AlertTriangle className="w-3 h-3" /> No pickup photo
                            </p>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-col gap-1">
                            {(reg.children || []).map((c, i) => (
                              <div key={i} className="text-xs">
                                <span className="text-[#F5F5F0]">{c.full_name}</span>
                                <span className="text-[#A1A1AA]"> · {c.age}y · {c.age_group}</span>
                                <div className="font-mono text-[#22C55E] font-bold">{c.ticket_code || "—"}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#F5F5F0]">{reg.num_children}</td>
                        <td className="py-4 px-6 text-[#F5F5F0] font-medium">C${(reg.total || 0).toFixed(2)}</td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 text-xs rounded ${src === "stripe" ? "bg-blue-900/30 text-blue-400" : "bg-purple-900/30 text-purple-400"}`}>
                            {SOURCE_LABELS[src] || src.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                            reg.payment_status === "paid" ? "bg-green-900/30 text-green-500"
                            : reg.payment_status === "pending" ? "bg-yellow-900/30 text-yellow-500"
                            : "bg-red-900/30 text-red-500"
                          }`}>{reg.payment_status}</span>
                        </td>
                        <td className="py-4 px-6 text-[#A1A1AA] text-sm">{formatDate(reg.created_at)}</td>
                        <td className="py-4 px-6">
                          <div className="flex gap-2">
                            <button onClick={() => setEditingReg(reg)} className="p-2 bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 rounded transition-colors" title="View details" data-testid={`view-${reg.id}`}>
                              <Eye className="w-4 h-4" />
                            </button>
                            {reg.payment_status !== "paid" && (
                              <button onClick={() => confirmBooking(reg.id)} className="p-2 bg-green-900/30 text-green-500 hover:bg-green-900/50 rounded transition-colors" title="Confirm payment & send ticket" data-testid={`confirm-${reg.id}`}>
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => resendEmail(reg.id)} className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded transition-colors" title="Resend email" data-testid={`resend-email-${reg.id}`}>
                              <Mail className="w-4 h-4" />
                            </button>
                            <button onClick={() => resendWhatsApp(reg.id)} className="p-2 bg-green-900/30 text-green-400 hover:bg-green-900/50 rounded transition-colors" title={whatsappConfigured ? "Send WhatsApp (automated)" : "Open WhatsApp (manual)"} data-testid={`resend-whatsapp-${reg.id}`}>
                              <MessageCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(reg.id)} className="p-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded transition-colors" title="Delete" data-testid={`delete-${reg.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Manual modal */}
      {showAddManual && (
        <ManualAddModal onClose={() => setShowAddManual(false)} onSuccess={() => { setShowAddManual(false); fetchData(); }} />
      )}

      {/* Detail / Edit modal */}
      {editingReg && <DetailModal reg={editingReg} onClose={() => setEditingReg(null)} />}

      {/* Single delete confirm */}
      {deleteId && (
        <ConfirmModal
          title="Delete registration?"
          message="This action cannot be undone."
          onCancel={() => setDeleteId(null)}
          onConfirm={deleteReg}
        />
      )}

      {/* Bulk delete confirm */}
      {showBulkDelete && (
        <ConfirmModal
          title={`Delete ${selectedIds.length} registrations?`}
          message="This will permanently delete all selected registrations. Cannot be undone."
          onCancel={() => setShowBulkDelete(false)}
          onConfirm={bulkDelete}
        />
      )}
    </AdminLayout>
  );
};

/* =====================
   Sub-components
   ===================== */

const ConfirmModal = ({ title, message, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-[#0A0A0A] border border-red-500/30 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-[#F5F5F0] mb-2">{title}</h3>
      <p className="text-[#A1A1AA] text-sm mb-4">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#F5F5F0]">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-red-500 text-white font-bold" data-testid="confirm-delete-btn">Delete</button>
      </div>
    </div>
  </div>
);

const DetailModal = ({ reg, onClose }) => {
  const hasIdPhoto = !!(reg.pickup_id_storage_path || "").trim();
  const hasSelfiePhoto = !!(reg.pickup_selfie_storage_path || "").trim();
  const idUrl = hasIdPhoto ? `${API}/admin/market-camp/pickup-photo/${reg.id}/id` : "";
  const selfieUrl = hasSelfiePhoto ? `${API}/admin/market-camp/pickup-photo/${reg.id}/selfie` : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6" onClick={e => e.stopPropagation()} data-testid="detail-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-[#D4AF37]">Registration · {reg.registration_code}</h3>
          <button onClick={onClose} className="p-1 text-[#A1A1AA] hover:text-[#F5F5F0]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-4 text-sm">
          <Field label="Parent" value={reg.parent_full_name} />
          <Field label="Email" value={reg.parent_email} />
          <Field label="Phone" value={reg.parent_phone} />
          <Field label="Status" value={reg.payment_status} />
          <Field label="Emergency #1" value={`${reg.emergency_contact_name || "—"} · ${reg.emergency_contact_phone || "—"}`} />
          <Field label="Emergency #2" value={reg.secondary_emergency_name ? `${reg.secondary_emergency_name} · ${reg.secondary_emergency_phone}` : "—"} />
          <Field label="Authorized pickup" value={reg.authorized_pickup} />
          <Field label="Total paid" value={`C$${(reg.total || 0).toFixed(2)}`} />
        </div>

        {/* Pickup Verification Photos */}
        <div className="mb-5 rounded-xl p-4 border border-[rgba(212,175,55,0.25)] bg-[#0F0F0F]" data-testid="pickup-verification-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[#D4AF37] font-bold text-sm uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Pickup Verification (Safety)
            </h4>
            {reg.pickup_verification_uploaded_at ? (
              <span className="text-[10px] text-[#A1A1AA]">Uploaded {formatDate(reg.pickup_verification_uploaded_at)}</span>
            ) : (
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">No photos uploaded</span>
            )}
          </div>
          <p className="text-xs text-[#A1A1AA] mb-3">
            Match the selfie to the person collecting the child. Verify the photo ID name matches an authorized pickup person above.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#52525B] mb-1.5">Photo ID</p>
              {hasIdPhoto ? (
                <a href={idUrl} target="_blank" rel="noopener noreferrer" data-testid="pickup-id-link">
                  <img
                    src={idUrl}
                    alt="Pickup photo ID"
                    className="w-full aspect-[4/3] object-cover rounded-lg border border-[rgba(212,175,55,0.3)] hover:border-[#D4AF37] transition-colors"
                    data-testid="pickup-id-image"
                  />
                  <p className="text-[10px] text-[#D4AF37] mt-1 underline">Open full-size</p>
                </a>
              ) : (
                <div className="w-full aspect-[4/3] rounded-lg border border-dashed border-[rgba(255,255,255,0.15)] flex items-center justify-center text-[#52525B] text-xs">
                  Not provided
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#52525B] mb-1.5">Selfie</p>
              {hasSelfiePhoto ? (
                <a href={selfieUrl} target="_blank" rel="noopener noreferrer" data-testid="pickup-selfie-link">
                  <img
                    src={selfieUrl}
                    alt="Pickup person selfie"
                    className="w-full aspect-[4/3] object-cover rounded-lg border border-[rgba(212,175,55,0.3)] hover:border-[#D4AF37] transition-colors"
                    data-testid="pickup-selfie-image"
                  />
                  <p className="text-[10px] text-[#D4AF37] mt-1 underline">Open full-size</p>
                </a>
              ) : (
                <div className="w-full aspect-[4/3] rounded-lg border border-dashed border-[rgba(255,255,255,0.15)] flex items-center justify-center text-[#52525B] text-xs">
                  Not provided
                </div>
              )}
            </div>
          </div>
        </div>

        <h4 className="text-[#D4AF37] font-bold text-sm mb-2 uppercase tracking-wider">Children &amp; Tickets</h4>
        <div className="space-y-3">
          {(reg.children || []).map((c, i) => (
            <div key={i} className="rounded-lg p-3 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
              <p className="font-bold text-[#F5F5F0]">{c.full_name} · age {c.age} · group {c.age_group}</p>
              <p className="font-mono text-[#22C55E] font-bold text-sm">Ticket #{c.ticket_code || "—"}</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-2 text-xs text-[#A1A1AA]">
                <p><strong className="text-red-400">Allergies:</strong> {c.allergies || "—"}</p>
                <p><strong>Dietary:</strong> {c.dietary_restrictions || "—"}</p>
                <p><strong>Medical:</strong> {c.medical_conditions || "—"}</p>
                <p><strong>Medications:</strong> {c.medications || "—"}</p>
                <p className="sm:col-span-2"><strong>Support needs:</strong> {c.support_needs || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-[#52525B] font-bold mb-1">{label}</p>
    <p className="text-[#F5F5F0]">{value || "—"}</p>
  </div>
);

const EMPTY_CHILD = { full_name: "", age: "", age_group: "", allergies: "", dietary_restrictions: "", medical_conditions: "", medications: "", support_needs: "", photo_consent: false };

const ManualAddModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    parent_full_name: "", parent_email: "", parent_phone: "",
    emergency_contact_name: "", emergency_contact_phone: "", authorized_pickup: "",
    source: "interac_e_transfer", payment_status: "paid", admin_notes: "",
    send_email: true, send_whatsapp: true,
  });
  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm(f => ({ ...f, [k]: !f[k] }));

  const updateChild = (i, k, v) => setChildren(c => {
    const next = [...c]; const u = { ...next[i], [k]: v };
    if (k === "age") {
      const a = parseInt(v, 10);
      u.age_group = a >= 4 && a <= 7 ? "4-7" : a >= 8 && a <= 11 ? "8-11" : a >= 12 && a <= 15 ? "12-15" : "";
    }
    next[i] = u; return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_full_name || !form.parent_email || !form.parent_phone) {
      toast.error("Parent name, email, and phone are required"); return;
    }
    for (let i = 0; i < children.length; i += 1) {
      if (!children[i].full_name || !children[i].age || !children[i].age_group) {
        toast.error(`Child ${i + 1} needs name + age (4–15)`); return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/manual`, { ...form, children });
      toast.success(`Registered · ${res.data.registration_code}`);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add registration");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6" data-testid="manual-add-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-[#D4AF37]">Add Guest Manually</h3>
          <button type="button" onClick={onClose} className="p-1 text-[#A1A1AA] hover:text-[#F5F5F0]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-4">For cash, Interac, or phone-in registrations — bypasses Stripe checkout.</p>

        <SectionLabel>Parent</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-3 mt-2 mb-4">
          <Input label="Full Name *" value={form.parent_full_name} onChange={change("parent_full_name")} testid="manual-parent-name" />
          <Input label="Email *" type="email" value={form.parent_email} onChange={change("parent_email")} testid="manual-parent-email" />
          <Input label="Phone *" value={form.parent_phone} onChange={change("parent_phone")} testid="manual-parent-phone" placeholder="e.g. 7805551234" />
          <Input label="Authorized Pickup" value={form.authorized_pickup} onChange={change("authorized_pickup")} placeholder="Names, comma-separated" />
          <Input label="Emergency Contact Name" value={form.emergency_contact_name} onChange={change("emergency_contact_name")} />
          <Input label="Emergency Contact Phone" value={form.emergency_contact_phone} onChange={change("emergency_contact_phone")} />
        </div>

        <SectionLabel>Children</SectionLabel>
        <div className="space-y-3 mb-4 mt-2">
          {children.map((c, i) => (
            <div key={i} className="rounded-lg p-3 bg-[#0F0F0F] border border-[rgba(212,175,55,0.15)]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold">Child {i + 1}</p>
                {children.length > 1 && (
                  <button type="button" onClick={() => setChildren(arr => arr.filter((_, idx) => idx !== i))} className="text-red-400 text-xs"><Trash className="w-4 h-4" /></button>
                )}
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                <Input label="Name *" value={c.full_name} onChange={e => updateChild(i, "full_name", e.target.value)} />
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1 font-bold">Age *</label>
                  <select value={c.age} onChange={e => updateChild(i, "age", e.target.value)} className="w-full px-2.5 py-2 rounded bg-[#050505] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] text-sm focus:border-[#D4AF37] outline-none">
                    <option value="">Select</option>
                    {[4,5,6,7,8,9,10,11,12,13,14,15].map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1 font-bold">Group</label>
                  <input value={c.age_group} disabled className="w-full px-2.5 py-2 rounded bg-[#050505] border border-[rgba(212,175,55,0.2)] text-[#D4AF37] text-sm" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <Input label="Allergies" value={c.allergies} onChange={e => updateChild(i, "allergies", e.target.value)} placeholder="or 'none'" />
                <Input label="Dietary" value={c.dietary_restrictions} onChange={e => updateChild(i, "dietary_restrictions", e.target.value)} placeholder="or 'none'" />
              </div>
            </div>
          ))}
          {children.length < 4 && (
            <button type="button" onClick={() => setChildren(c => [...c, { ...EMPTY_CHILD }])} className="text-[#D4AF37] text-sm font-bold inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add another child</button>
          )}
        </div>

        <SectionLabel>Payment</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-3 mt-2 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1 font-bold">Source</label>
            <select value={form.source} onChange={change("source")} className="w-full px-3 py-2 rounded bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] text-sm focus:border-[#D4AF37] outline-none">
              <option value="interac_e_transfer">Interac e-Transfer</option>
              <option value="cash">Cash</option>
              <option value="manual">Manual / Other</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1 font-bold">Status</label>
            <select value={form.payment_status} onChange={change("payment_status")} className="w-full px-3 py-2 rounded bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] text-sm focus:border-[#D4AF37] outline-none">
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
        <Input label="Admin Notes" value={form.admin_notes} onChange={change("admin_notes")} placeholder="e.g. Paid Jun 14 via e-Transfer ref #ABC" />

        <div className="flex gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm text-[#F5F5F0]">
            <input type="checkbox" checked={form.send_email} onChange={toggle("send_email")} className="rounded" style={{ accentColor: "#D4AF37" }} /> Send confirmation email
          </label>
          <label className="flex items-center gap-2 text-sm text-[#F5F5F0]">
            <input type="checkbox" checked={form.send_whatsapp} onChange={toggle("send_whatsapp")} className="rounded" style={{ accentColor: "#D4AF37" }} /> Send WhatsApp confirmation
          </label>
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t border-[rgba(212,175,55,0.15)] mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#F5F5F0]">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="manual-submit-btn">
            <Send className="w-4 h-4" /> {saving ? "Saving…" : "Add Registration"}
          </button>
        </div>
      </form>
    </div>
  );
};

const SectionLabel = ({ children }) => <h4 className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold pt-1">{children}</h4>;

const Input = ({ label, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1 font-bold">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2 rounded bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] text-sm focus:border-[#D4AF37] outline-none placeholder:text-[#52525B]"
      data-testid={testid} />
  </div>
);

// ════════════════════════════════════════════════════════════════
// 📊 MARKET INTELLIGENCE CHART · Booking distribution by age group
// ════════════════════════════════════════════════════════════════
const AGE_GROUPS = [
  { key: "4-7",   label: "Ages 4–7",   icon: "🎨", color: "#EF4444", desc: "Tiny Makers" },
  { key: "8-11",  label: "Ages 8–11",  icon: "⭐", color: "#D4AF37", desc: "Junior Creators" },
  { key: "12-15", label: "Ages 12–15", icon: "🚀", color: "#3FA437", desc: "Teen Entrepreneurs" },
];

const MarketIntelligenceChart = ({ stats }) => {
  const { counts, revenueByGroup, totalKids } = stats;
  if (totalKids === 0) {
    return (
      <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6" data-testid="market-intelligence-chart">
        <div className="flex items-center gap-3 mb-3">
          <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-base sm:text-lg font-bold text-[#F5F5F0]">Market Intelligence · Age Group Demand</h2>
        </div>
        <p className="text-[#A1A1AA] text-sm">No paid bookings yet — chart will populate as registrations roll in.</p>
      </div>
    );
  }
  const max = Math.max(...AGE_GROUPS.map(g => counts[g.key] || 0)) || 1;
  const topGroup = AGE_GROUPS.reduce((best, g) => (counts[g.key] > counts[best.key] ? g : best), AGE_GROUPS[0]);
  const topPct = totalKids ? Math.round((counts[topGroup.key] / totalKids) * 100) : 0;

  return (
    <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6" data-testid="market-intelligence-chart">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
          <div>
            <h2 className="text-base sm:text-lg font-bold text-[#F5F5F0]">Market Intelligence · Age Group Demand</h2>
            <p className="text-[11px] text-[#A1A1AA]">Which age bracket is booking the most? Use this to tune marketing spend &amp; capacity.</p>
          </div>
        </div>
        <div className="px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider" style={{ background: `${topGroup.color}20`, color: topGroup.color }} data-testid="mi-top-group">
          {topGroup.icon} {topGroup.label} leading · {topPct}%
        </div>
      </div>

      {/* Bar chart rows */}
      <div className="space-y-3">
        {AGE_GROUPS.map((g) => {
          const count = counts[g.key] || 0;
          const pct = totalKids ? (count / totalKids) * 100 : 0;
          const barPct = (count / max) * 100;
          const revenue = revenueByGroup[g.key] || 0;
          return (
            <div key={g.key} data-testid={`mi-row-${g.key}`}>
              <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-base leading-none">{g.icon}</span>
                  <span className="text-[#F5F5F0] font-bold text-sm">{g.label}</span>
                  <span className="text-[#A1A1AA] text-[11px] italic">· {g.desc}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-[#A1A1AA]"><strong className="text-[#F5F5F0]">{count}</strong> kid{count === 1 ? "" : "s"}</span>
                  <span className="text-[#A1A1AA]"><strong className="text-[#F5F5F0]">{pct.toFixed(0)}%</strong> share</span>
                  <span className="text-[#A1A1AA]">C$<strong className="text-[#D4AF37]">{revenue.toFixed(0)}</strong></span>
                </div>
              </div>
              <div className="w-full h-7 bg-[#0A0A0A] border border-[rgba(212,175,55,0.1)] rounded overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-700 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${barPct}%`, background: `linear-gradient(90deg, ${g.color}80 0%, ${g.color} 100%)` }}
                >
                  {barPct > 12 && <span className="text-[10px] font-extrabold text-white drop-shadow">{count}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)] grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
        <Insight label="Total kids" value={totalKids} color="#F5F5F0" />
        <Insight label="Top bracket" value={topGroup.label} color={topGroup.color} />
        <Insight label="Top share" value={`${topPct}%`} color={topGroup.color} />
        <Insight
          label="Brackets active"
          value={AGE_GROUPS.filter(g => (counts[g.key] || 0) > 0).length + "/" + AGE_GROUPS.length}
          color="#D4AF37"
        />
      </div>
    </div>
  );
};

const Insight = ({ label, value, color }) => (
  <div className="bg-[#0A0A0A] border border-[rgba(212,175,55,0.1)] rounded p-2">
    <p className="text-[9px] uppercase tracking-wider text-[#52525B] font-bold">{label}</p>
    <p className="text-sm font-extrabold mt-0.5" style={{ color }}>{value}</p>
  </div>
);

export default AdminMarketCampRegistrations;
